import { Component, OnDestroy, OnInit } from '@angular/core';
import {
    AuthorityRegisterSummaryDto,
    AuthorityTypeDto,
    FEATURE_OUTSTANDING_TASKS_VIEW,
    OrganisationAuthorityDto,
    OrganisationAuthorityGroup,
    PersonsOrganisationDto,
    SimpleAuthorityTypeDto,
    hasFeatureFlag,
    PreferencesService
} from '@flyfreely-portal-ui/flyfreely';
import {
    ColumnSortPreferences,
    TableColumn,
    TableConfig
} from '@flyfreely-portal-ui/flyfreely-table';
import { FormatDatePipe } from '@flyfreely-portal-ui/ui';
import { WorkspaceStateService } from '@flyfreely-portal-ui/workspace';
import {
    collapseOnLeaveAnimation,
    expandOnEnterAnimation
} from 'angular-animations';
import { AuthorityDialoguesService } from 'libs/authorities/src/lib/authority-dialogues.service';
import { RegisterExpirySummary } from 'libs/authorities/src/lib/organisation-authority/organisation-authority-edit.service';
import { EMPTY, Subject } from 'rxjs';
import { takeUntil, catchError } from 'rxjs/operators';
import {
    OutstandingTasksViewTableSetupUserPreferences,
    WidgetRegistrationWithSection
} from '../../../outstanding-tasks-view.service';
import {
    AuthorityTaskWidgetsDataService,
    formatAuthorityExpiryWidgetStatuses
} from '../authority-data.service';
import { applyPreferences } from 'libs/flyfreely-table/src/lib/helpers';

const ORGANISATION_AUTHORITY_EXPIRY_IDENTIFIER =
    'organisationAuthoritiesExpiryTaskWidget';

interface TableAuthority {
    name: string;
    identifier: string;
    startDate: string;
    expiryDate: string;
    status: OrganisationAuthorityDto.ExpiryStatus;
    authority: OrganisationAuthorityDto;
    authorityType: SimpleAuthorityTypeDto;
}

@Component({
    selector: 'organisation-authorisation-expiries-widget',
    templateUrl: './organisation-authority-expiries-widget.component.html',
    styleUrls: ['../../widget-styles.scss'],
    animations: [expandOnEnterAnimation(), collapseOnLeaveAnimation()]
})
export class OrganisationAuthorityExpiriesWidgetComponent
    implements OnInit, OnDestroy
{
    organisation: PersonsOrganisationDto;
    showBody = true;

    authoritiesWithExpiry: TableAuthority[];
    registerSummaries: RegisterExpirySummary[];

    formatStatuses = formatAuthorityExpiryWidgetStatuses;

    //Static table variables
    availableColumns: TableColumn[];
    selectedColumns: string[];
    tableConfig: TableConfig;
    columnSorting: ColumnSortPreferences;
    userPreferences: OutstandingTasksViewTableSetupUserPreferences;

    working = false;
    private ngUnsubscribe$ = new Subject<void>();
    constructor(
        private authorityDataService: AuthorityTaskWidgetsDataService,
        private workspaceStateService: WorkspaceStateService,
        private authorityDialoguesService: AuthorityDialoguesService,
        private formatDatePipe: FormatDatePipe,
        private preferencesService: PreferencesService
    ) {
        this.availableColumns = [
            {
                value: 'name',
                name: 'Authority',
                searchable: false,
                selectable: false,
                defaultSelection: true,
                linkFunction: row => this.viewAuthority(row)
            },
            {
                value: 'identifier',
                name: 'Identifier',
                searchable: false,
                selectable: false,
                defaultSelection: true
            },
            {
                value: 'startDate',
                name: 'Start Date',
                searchable: false,
                selectable: false,
                defaultSelection: true,
                formatterFunction: d => this.formatDatePipe.transform(d)
            },
            {
                value: 'expiryDate',
                name: 'Expiry Date',
                searchable: false,
                selectable: false,
                defaultSelection: true
            },
            {
                value: 'status',
                name: 'Status',
                searchable: false,
                selectable: false,
                defaultSelection: true
            }
        ];

        this.selectedColumns = null;

        this.tableConfig = {
            limit: 3,
            actions: [],
            limitSelection: [3, 10, 25, 50, 100]
        };

        this.authorityDataService.working$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));
    }

    ngOnInit() {
        this.workspaceStateService.currentLoadedOrganisation$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(organisation => {
                this.organisation = organisation.organisation;
                this.loadPreferences();
            });
        this.authorityDataService.currentOrganisationAuthorities$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(authorities => {
                this.buildTableData(authorities);
            });
        this.authorityDataService.authorityRegisterExpiries$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(registerSummaries => {
                this.registerSummaries = registerSummaries;
            });
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    onToggleCollapse() {
        this.showBody = !this.showBody;
        this.userPreferences = {
            ...this.userPreferences,
            showBody: this.showBody
        };
        this.updateUserPreferences();
    }

    private loadPreferences() {
        if (!this.organisation) {
            return;
        }
        this.preferencesService
            .findPreferences<OutstandingTasksViewTableSetupUserPreferences>(
                'organisationAuthorityExpiriesWidgetTable',
                this.organisation.id
            )
            .pipe(
                catchError(() => {
                    this.handleDefaultPreferences();
                    return EMPTY;
                })
            )
            .subscribe(preferences => {
                if (preferences === null) {
                    this.handleDefaultPreferences();
                } else {
                    this.applyPreferences(preferences);
                }
            });
    }

    private handleDefaultPreferences() {
        this.tableConfig.limit = 3; // reset limit to default
        this.applyPreferences({});
    }

    private applyPreferences(
        preferences: OutstandingTasksViewTableSetupUserPreferences
    ) {
        this.userPreferences = applyPreferences(
            preferences,
            this.availableColumns,
            this.tableConfig
        );
        this.selectedColumns = this.userPreferences.selectedColumns;
        this.columnSorting = this.userPreferences.columnSorting;
        this.tableConfig.limit = this.userPreferences.itemLimit;
        this.showBody = this.userPreferences.showBody ?? true;
    }

    updateSelectedColumns(selectedColumns: string[]) {
        this.selectedColumns = selectedColumns;
        if (
            this.columnSorting != null &&
            !this.selectedColumns.includes(this.columnSorting.column)
        ) {
            this.columnSorting = null;
        }
        this.userPreferences = {
            ...this.userPreferences,
            selectedColumns: selectedColumns,
            columnSorting: this.columnSorting || null
        };
        this.updateUserPreferences();
    }

    updateColumnSorting(sorting: ColumnSortPreferences) {
        this.columnSorting = sorting;
        this.userPreferences = {
            ...this.userPreferences,
            columnSorting: sorting
        };
        this.updateUserPreferences();
    }

    updateItemLimit(limit: number) {
        this.tableConfig.limit = limit;
        this.userPreferences = {
            ...this.userPreferences,
            itemLimit: limit
        };
        this.updateUserPreferences();
    }

    updateUserPreferences() {
        this.preferencesService
            .updatePreferences(
                'organisationAuthorityExpiriesWidgetTable',
                this.organisation.id,
                this.userPreferences
            )
            .subscribe();
    }

    /**
     * Takes the full list of authorities for the organsiation and filters out only authorities with expiry dates,
     * then recompiles them into a list of authorities displayable by the table.
     * @param authorities all of the authority groups for the organisation
     */
    buildTableData(authorities: OrganisationAuthorityGroup[]) {
        const withExpiries = authorities.reduce(
            (acc: TableAuthority[], authorityGroup) => {
                const authoritiesWithExpiry = authorityGroup.authorities
                    .filter(
                        a =>
                            a.expiryDate != null &&
                            (a.expiryStatus === 'EXPIRED' ||
                                a.expiryStatus === 'UPCOMING')
                    )
                    .map(
                        authority =>
                            <TableAuthority>{
                                authority: authority,
                                authorityType: <SimpleAuthorityTypeDto>(
                                    authorityGroup
                                ),
                                name: authorityGroup.name,
                                identifier: authority.identifier,
                                startDate: authority.startDate,
                                expiryDate: authority.expiryDate,
                                status: authority.expiryStatus
                            }
                    );
                return acc.concat(authoritiesWithExpiry);
            },
            []
        );
        this.authoritiesWithExpiry = withExpiries;
    }

    viewAuthority(authority: TableAuthority) {
        const modal = this.authorityDialoguesService.showAuthorityDetails(
            this.organisation.id,
            authority.authorityType,
            authority.authority,
            null,
            true,
            true,
            this.registerSummaries
        );
        modal.content.onShowRegister
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(payload => {
                this.showRegister(
                    <AuthorityTypeDto>payload.authorityType,
                    payload.authority,
                    payload.register
                );
            });
    }
    showRegister(
        authorityType: AuthorityTypeDto,
        authority: OrganisationAuthorityDto,
        register: AuthorityRegisterSummaryDto
    ) {
        this.authorityDialoguesService.showPersonnelRegisterDialogue(
            this.organisation.id,
            authorityType,
            <OrganisationAuthorityDto>authority,
            register,
            this.organisation.id
        );
    }
}

export const organisationAuthorityExpiryTaskWidgetRegistration: WidgetRegistrationWithSection =
    {
        widgetIdentifier: ORGANISATION_AUTHORITY_EXPIRY_IDENTIFIER,
        section: 'organisation',
        component: OrganisationAuthorityExpiriesWidgetComponent,
        isAvailable: organisation =>
            hasFeatureFlag(organisation, FEATURE_OUTSTANDING_TASKS_VIEW)
    };
